import { toast } from 'sonner';
import { useDebouncedCallback } from 'use-debounce';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { queryClient } from '@/queryclient';
import type { Hunt, HuntStatus } from '@/types';
import { useMutation } from '@/utils/api';

// Hunt resources are driven by WebSocket subscriptions, mirroring the
// useChat pattern. The 60s fallback poll is a safety net for missed
// events (WS disconnects, redeploys, etc.) — the WS layer is the
// primary delivery path. Invalidations are debounced 2s so a burst of
// events from a single iteration (memory writes, planner messages)
// collapses to one refetch.
const FALLBACK_POLL_MS = 60_000;
const INVALIDATE_DEBOUNCE_MS = 2_000;

/**
 * useHunts lists every hunt in the current tenant via `/my?key=#hunt`.
 *
 * Subscribes to `#hunt#` so sweep-driven changes (iterationCount,
 * status, currentConvId, lastError) surface immediately.
 */
export const useHunts = () => {
  const query = useMy(
    { resource: 'hunt' },
    { refetchInterval: FALLBACK_POLL_MS }
  );
  const debouncedInvalidate = useDebouncedCallback(
    () => query.invalidate(),
    INVALIDATE_DEBOUNCE_MS
  );
  useRealtimeSubscription({
    patterns: [{ pattern: '#hunt#', matchType: 'prefix' }],
    onUpdate: debouncedInvalidate,
  });
  return query;
};

/**
 * useHuntMemory lists the agent's atomic memory items for a hunt.
 *
 * Each `PutMemory` call from the agent persists a `File` row under the
 * `memory/hunt/<uuid>/` namespace (see `backend/pkg/services/memory/`),
 * so a prefix search on `#file#memory/hunt/<uuid>/` is the listing.
 * Subscribes to the same prefix on WS so memory writes from the agent
 * stream in without waiting on the fallback poll.
 */
export const useHuntMemory = (uuid: string | undefined) => {
  const query = useMy(
    {
      resource: 'file',
      query: uuid ? `memory/hunt/${uuid}/` : undefined,
    },
    { enabled: !!uuid, refetchInterval: FALLBACK_POLL_MS }
  );
  const debouncedInvalidate = useDebouncedCallback(
    () => query.invalidate(),
    INVALIDATE_DEBOUNCE_MS
  );
  useRealtimeSubscription({
    patterns: uuid
      ? [{ pattern: `#file#memory/hunt/${uuid}/`, matchType: 'prefix' }]
      : [],
    enabled: !!uuid,
    onUpdate: debouncedInvalidate,
  });
  return query;
};

/**
 * useHuntConversations lists the per-iteration transcripts that the
 * cron sweep mints for a hunt. `user: false` because hunt iterations
 * live in the tenant (Public) partition, not the per-user one.
 *
 * The table key for a hunt's iteration conversations is `#conversation#`
 * keyed by conversation UUID, with the hunt link stored as the `huntId`
 * body field. We subscribe to the broad `#conversation#` prefix and let
 * the server-side filter (`hunt:<uuid>` GSI) narrow on each refetch —
 * the debounced invalidate coalesces unrelated-conversation events into
 * a single refresh per burst.
 */
export const useHuntConversations = (uuid: string | undefined) => {
  const query = useMy(
    {
      resource: 'conversation',
      query: uuid ? `hunt:${uuid}` : undefined,
      user: false,
    },
    { enabled: !!uuid, refetchInterval: FALLBACK_POLL_MS }
  );
  const debouncedInvalidate = useDebouncedCallback(
    () => query.invalidate(),
    INVALIDATE_DEBOUNCE_MS
  );
  useRealtimeSubscription({
    patterns: [{ pattern: '#conversation#', matchType: 'prefix' }],
    enabled: !!uuid,
    onUpdate: debouncedInvalidate,
  });
  return query;
};

/**
 * useHuntMessages reads the messages for a hunt iteration.
 *
 * We use a key-prefix query (`#message#<convId>`) instead of the
 * Conversation GSI because the tabularium `Message` model stores its
 * foreign key as `conversationId` while the GSI expects `conversation`.
 * `user: false` keeps us in the tenant partition where hunt
 * conversations live. The WS subscription scopes to this conversation's
 * message prefix so we only refetch when the active iteration moves.
 */
export const useHuntMessages = (convId: string | undefined) => {
  const query = useMy(
    {
      resource: 'message',
      query: convId,
      user: false,
    },
    { enabled: !!convId, refetchInterval: FALLBACK_POLL_MS }
  );
  const debouncedInvalidate = useDebouncedCallback(
    () => query.invalidate(),
    INVALIDATE_DEBOUNCE_MS
  );
  useRealtimeSubscription({
    patterns: convId
      ? [{ pattern: `#message#${convId}`, matchType: 'prefix' }]
      : [],
    enabled: !!convId,
    onUpdate: debouncedInvalidate,
  });
  return query;
};

interface CreateHuntPayload {
  prompt: string;
  expiresAt: string;
  finishCriteria?: string;
  agent?: string;
  allowedTools?: string[];
  scope?: string[];
}

const invalidateHuntList = () =>
  queryClient.invalidateQueries({ predicate: matchesMyResource('hunt') });

const invalidateFileList = () =>
  queryClient.invalidateQueries({ predicate: matchesMyResource('file') });

const invalidateMessageList = () =>
  queryClient.invalidateQueries({ predicate: matchesMyResource('message') });

// Match the `useMy`-built cache keys without taking a dependency on the
// hook's internal `getQueryKey` shape. `useMy` keys start with
// `['MY', '<resource>', ...]`; touching that prefix is enough to refresh
// every variant (impersonation, query string, etc.) at once.
function matchesMyResource(resource: 'hunt' | 'file' | 'message') {
  return (query: { queryKey: readonly unknown[] }) =>
    query.queryKey[0] === 'MY' && query.queryKey[1] === resource;
}

/**
 * useCreateHunt opens a new persistent hunt. Praetorian-only on the
 * server; non-Praetorian calls get a 403 surfaced via the default toast.
 */
export const useCreateHunt = () => {
  const axios = useAxios();

  return useMutation<Hunt, Error, CreateHuntPayload>({
    defaultErrorMessage: 'Failed to create hunt',
    mutationFn: async payload => {
      const { data } = await axios.post<Hunt>('/hunt', payload);
      return data;
    },
    onSuccess: () => {
      toast.success('Hunt created');
      invalidateHuntList();
    },
  });
};

export const useUpdateHuntStatus = () => {
  const axios = useAxios();

  return useMutation<Hunt, Error, { uuid: string; status: HuntStatus }>({
    defaultErrorMessage: 'Failed to update hunt',
    mutationFn: async ({ uuid, status }) => {
      const { data } = await axios.put<Hunt>(`/hunt/${uuid}`, { status });
      return data;
    },
    onSuccess: (_data, variables) => {
      const message =
        variables.status === 'stopped'
          ? 'Hunt stopped'
          : variables.status === 'paused'
            ? 'Hunt paused'
            : 'Hunt resumed';
      toast.success(message);
      invalidateHuntList();
    },
  });
};

/**
 * Memory writes/deletes go through the dedicated
 * `/hunt/{uuid}/memory/{title}` endpoint so the hunt service owns the
 * S3 + DDB-index contract end-to-end. Callers read individual items via
 * `useGetFile({ name: 'memory/hunt/<uuid>/<title>' })`.
 */
export const useSaveHuntMemory = (uuid: string | undefined) => {
  const axios = useAxios();

  return useMutation<unknown, Error, { title: string; content: string }>({
    defaultErrorMessage: 'Failed to save memory',
    mutationFn: async ({ title, content }) => {
      await axios.put(`/hunt/${uuid}/memory/${encodeURIComponent(title)}`, {
        content,
      });
    },
    onSuccess: (_data, variables) => {
      toast.success(`Saved ${variables.title}`);
      invalidateFileList();
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getFile({
          name: `memory/hunt/${uuid}/${variables.title}`,
        }),
      });
    },
  });
};

export const useDeleteHuntMemory = (uuid: string | undefined) => {
  const axios = useAxios();

  return useMutation<unknown, Error, { title: string }>({
    defaultErrorMessage: 'Failed to delete memory',
    mutationFn: async ({ title }) => {
      await axios.delete(`/hunt/${uuid}/memory/${encodeURIComponent(title)}`);
    },
    onSuccess: (_data, variables) => {
      toast.success(`Deleted ${variables.title}`);
      invalidateFileList();
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getFile({
          name: `memory/hunt/${uuid}/${variables.title}`,
        }),
      });
    },
  });
};

/**
 * Send a guidance message to a currently-running hunt iteration. The
 * planner endpoint enqueues the message; the agent picks it up at the
 * next tool turn.
 */
export const useSendHuntMessage = (convId: string | undefined) => {
  const axios = useAxios();

  return useMutation<unknown, Error, { message: string }>({
    defaultErrorMessage: 'Failed to send message',
    mutationFn: async ({ message }) => {
      const { data } = await axios.post('/planner', {
        conversationId: convId,
        message,
      });
      return data;
    },
    onSuccess: () => {
      toast.success('Message queued for agent');
      invalidateMessageList();
    },
  });
};
